import React, { useEffect, useState } from "react";
import axios from "axios";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import { API } from "../AuthContext";

function Section({ eyebrow, title, children }) {
  return (
    <section style={{ marginBottom: 48 }} data-testid={`privacy-section-${eyebrow.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="eyebrow" style={{ marginBottom: 12 }}>{eyebrow}</div>
      <h2
        className="serif-italic"
        style={{ fontSize: 26, fontWeight: 500, color: "#1a1008", margin: "0 0 16px", lineHeight: 1.25 }}
      >
        {title}
      </h2>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic", fontWeight: 500, fontSize: 18, lineHeight: 1.7, color: "#1a1008" }}>
        {children}
      </div>
    </section>
  );
}

export default function Privacy() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/privacy/info`);
        setInfo(data);
      } catch {}
    })();
  }, []);

  const contact = info?.contact_email || "privacy@111collective.app";
  const today = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="page" data-testid="privacy-page">
      <div className="grain-bg" />
      <div className="container-narrow">
        <AppHeader />

        <div className="fade-in">
          <div className="eyebrow" style={{ marginBottom: 16 }}>Privacy</div>
          <h1 className="display" data-testid="privacy-heading">How your reframes stay yours.</h1>

          <p
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: 19,
              lineHeight: 1.7,
              color: "#1a1008",
              marginBottom: 12,
            }}
          >
            Plain language. No legalese. Last updated {today}.
          </p>

          <div className="divider" />

          <Section eyebrow="What we collect" title="The minimum to make this work.">
            <p style={{ margin: "0 0 14px" }}>
              When you create an account we collect your <strong>email address</strong> and the
              <strong> password you choose</strong> (which we never store as plain text — it's hashed with bcrypt).
            </p>
            <p style={{ margin: 0 }}>
              When you use the app we store the <strong>thoughts you write</strong> and the
              <strong> reframes generated for you</strong>. That's it. No analytics on the content of your entries.
            </p>
          </Section>

          <Section eyebrow="Where it lives" title="Our secure database. Encrypted at rest.">
            <p style={{ margin: "0 0 14px" }}>
              Your entries live in a managed MongoDB database that only our backend can reach.
              Every reframe — the thought, the heading, the body, the anchor — is
              <strong> encrypted before it touches the disk</strong> using authenticated symmetric encryption (Fernet / AES-128-CBC with HMAC-SHA256).
            </p>
            <p style={{ margin: 0 }}>
              Even if someone got a copy of the database file, your entries would read as random text without the key, which lives only on our server.
            </p>
          </Section>

          <Section eyebrow="Who can see it" title="You. Only ever you.">
            <p style={{ margin: "0 0 14px" }}>
              Every read, write, and delete is scoped to your authenticated user ID on the server. There is
              <strong> no community feed, no sharing, no "see what others reframed today" </strong>
              feature — and there never will be. This product has no social surface area by design.
            </p>
            <p style={{ margin: 0 }}>
              Our team does not browse user journals. We have no admin "view as user" tool. If we ever need to
              debug a specific bug a user reports, we ask first, work only on the metadata, and never decrypt entries.
            </p>
          </Section>

          <Section eyebrow="What we will never do" title="No selling. No sharing. No AI training.">
            <p style={{ margin: 0 }}>
              We will never sell your entries. We will never share them with advertisers, data brokers, or any
              third party. <strong>We will never use your entries to train an AI model</strong> — ours or anyone else's.
              If that ever changes, this page will say so first, and you'll get an email before it does.
            </p>
          </Section>

          <Section eyebrow="The Anthropic call" title="Your thought is sent to Claude — and only Claude.">
            <p style={{ margin: "0 0 14px" }}>
              To generate a reframe, your thought is sent from our backend to <strong>Anthropic's Claude API</strong>.
              The Anthropic API key is stored as an environment variable on our server — it is
              <strong> never exposed in the frontend code</strong> or in any response sent to your browser.
            </p>
            <p style={{ margin: 0 }}>
              Per Anthropic's published API terms,
              <strong> Anthropic does not use API inputs or outputs to train its models</strong>,
              and prompts/responses are not retained beyond what is required to deliver the response.
              You can read their terms at <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noreferrer" style={{ borderBottom: "1px solid currentColor" }}>anthropic.com/legal/commercial-terms</a>.
            </p>
          </Section>

          <Section eyebrow="Your controls" title="Delete anything. Delete everything. Any time.">
            <p style={{ margin: "0 0 14px" }}>
              In your journal, every entry has a <strong>Remove</strong> link that permanently deletes it from our database.
            </p>
            <p style={{ margin: 0 }}>
              On your account page, the <strong>Delete account</strong> button permanently erases your account
              and every reframe, every journal entry, every payment record, every session.
              It cannot be undone, and we don't keep "soft delete" copies.
            </p>
          </Section>

          <Section eyebrow="Payments" title="Stripe handles the card. We never see it.">
            <p style={{ margin: 0 }}>
              Subscription payments go directly to Stripe. We receive only the result of the charge
              (success/failure, plan, expiry). Your card number, CVC, and billing address are handled by Stripe and
              never touch our servers.
            </p>
          </Section>

          <Section eyebrow="Contact" title="A real human reads these.">
            <p style={{ margin: 0 }}>
              Questions about privacy, requests to export your data, or anything else?
              Write to <a href={`mailto:${contact}`} style={{ borderBottom: "1px solid currentColor" }} data-testid="privacy-contact-email">{contact}</a>.
            </p>
          </Section>

          <SiteFooter />
        </div>
      </div>
    </div>
  );
}
